import { BaseThreeScene } from '@modules/three/common/BaseThreeScene';
import type { JawsSegmentationResponse } from '@/schema/jawsSegmentation';
import type { JawsCorrectionResponse } from '@/schema/JawsCorrection';
import type { ToothSegmentationResponse } from '@/schema/toothSegmentation';
import JawExport from '@export/three/JawExport.ts';
import JawsExport from '@export/three/JawsExport.ts';
import { BufferGeometry } from 'three';

type TeethBundle = {
  geometries: BufferGeometry[];
  data: ToothSegmentationResponse[];
};

export default class ExportService {
  private prepared = false;
  private upperTeeth: TeethBundle = { geometries: [], data: [] };
  private lowerTeeth: TeethBundle = { geometries: [], data: [] };
  private gumCache = new Map<string, { upper: BufferGeometry; lower: BufferGeometry }>();

  public async prepare(segmentation: JawsSegmentationResponse) {
    this.upperTeeth = {
      data: segmentation.upperJaw.teethSegmentation,
      geometries: await BaseThreeScene.loadGeometries(
        segmentation.upperJaw.teethSegmentation.map((tooth) => tooth.model3dUrl),
      ),
    };

    this.lowerTeeth = {
      data: segmentation.lowerJaw.teethSegmentation,
      geometries: await BaseThreeScene.loadGeometries(
        segmentation.lowerJaw.teethSegmentation.map((tooth) => tooth.model3dUrl),
      ),
    };

    this.prepared = true;
  }

  private cloneGeometries(source: BufferGeometry[]) {
    return source.map((geometry) => geometry.clone());
  }

  private async loadGumsForCorrection(correction: JawsCorrectionResponse) {
    const cached = this.gumCache.get(correction.id);
    if (cached) {
      return { upper: cached.upper.clone(), lower: cached.lower.clone() };
    }

    const [upper] = await BaseThreeScene.loadGeometries([correction.upperJaw.model3dUrl]);
    const [lower] = await BaseThreeScene.loadGeometries([correction.lowerJaw.model3dUrl]);
    this.gumCache.set(correction.id, { upper, lower });
    return { upper: upper.clone(), lower: lower.clone() };
  }

  public async createJawsExport(correction: JawsCorrectionResponse | null): Promise<JawsExport | null> {
    if (!this.prepared || !correction) {
      return null;
    }

    const gums = await this.loadGumsForCorrection(correction);
    const upperJaw = new JawExport(
      gums.upper,
      this.cloneGeometries(this.upperTeeth.geometries),
      this.upperTeeth.data,
      'upper',
    );
    const lowerJaw = new JawExport(
      gums.lower,
      this.cloneGeometries(this.lowerTeeth.geometries),
      this.lowerTeeth.data,
      'lower',
    );

    const jawsExport = new JawsExport(lowerJaw, upperJaw);
    jawsExport.applyCorrection(correction);
    return jawsExport;
  }

  public disposeJaws(jaws: JawsExport | undefined | null) {
    if (!jaws) return;
    jaws.dispose();
  }

  public dispose() {
    this.gumCache.clear();
    this.prepared = false;
  }
}

